import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { useState } from "react";
import { Link } from "wouter";

function providerLabel(provider: string) {
  if (provider === "aio") return "AIO";
  if (provider === "gemini") return "Gemini";
  if (provider === "chatgpt") return "ChatGPT";
  return provider;
}

function citationTypeLabel(type: string) {
  if (type === "brand_mentioned") return "品牌提及";
  if (type === "brand_linked") return "品牌連結";
  return type;
}

export default function AiPerformancePages() {
  const { loading } = useAuth();
  const [days, setDays] = useState<7 | 30 | 90>(30);
  const [provider, setProvider] = useState<"all" | "aio" | "gemini" | "chatgpt">("all");
  const [citationType, setCitationType] = useState<"all" | "brand_mentioned" | "brand_linked">("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const { data, isLoading } = trpc.dashboard.getAiPerformancePageList.useQuery({
    days,
    provider: provider === "all" ? undefined : provider,
    citationType: citationType === "all" ? undefined : citationType,
    search: search || undefined,
    page,
    pageSize,
  });

  if (loading) {
    return <DashboardLayout><div className="p-8">載入中...</div></DashboardLayout>;
  }

  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <DashboardLayout>
      <div className="container py-8 space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold">被引用頁面完整列表</h1>
            <p className="text-muted-foreground">僅顯示命中網站網域的引用頁面</p>
          </div>
          <Link href="/ai-performance">
            <Button variant="outline">返回 AI 引用表現</Button>
          </Link>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>篩選條件</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2 items-center">
            {[7, 30, 90].map((v) => (
              <Button key={v} size="sm" variant={days === v ? "default" : "outline"} onClick={() => { setDays(v as 7 | 30 | 90); setPage(1); }}>
                {v} 天
              </Button>
            ))}
            <select className="h-9 rounded-md border bg-background px-2 text-sm" value={provider} onChange={(e) => { setProvider(e.target.value as any); setPage(1); }}>
              <option value="all">來源：全部</option>
              <option value="aio">來源：AIO</option>
              <option value="gemini">來源：Gemini</option>
              <option value="chatgpt">來源：ChatGPT</option>
            </select>
            <select className="h-9 rounded-md border bg-background px-2 text-sm" value={citationType} onChange={(e) => { setCitationType(e.target.value as any); setPage(1); }}>
              <option value="all">引用類型：全部</option>
              <option value="brand_mentioned">品牌提及</option>
              <option value="brand_linked">品牌連結</option>
            </select>
            <input
              className="h-9 rounded-md border bg-background px-3 text-sm w-[260px]"
              placeholder="搜尋頁面 URL"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>結果</CardTitle>
            <CardDescription>共 {total} 筆</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-sm text-muted-foreground">載入中...</div>
            ) : (data?.items || []).length === 0 ? (
              <div className="text-sm text-muted-foreground">暫無資料</div>
            ) : (
              <div className="space-y-2">
                {(data?.items || []).map((item: any) => (
                  <div key={item.url} className="rounded-md border p-3">
                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="font-medium hover:underline break-all">
                      {item.url}
                    </a>
                    <div className="text-sm text-muted-foreground mt-1">
                      引用次數 {item.citations} | 最近引用 {item.lastSeen ? new Date(item.lastSeen).toLocaleString("zh-TW") : "-"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      關聯關鍵字：{(item.keywords || []).join("、") || "-"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      來源：{(item.providers || []).map((p: string) => providerLabel(p)).join("、") || "-"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      類型：{(item.citationTypes || []).map((t: string) => citationTypeLabel(t)).join("、") || "-"}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between mt-4">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                上一頁
              </Button>
              <div className="text-sm text-muted-foreground">第 {page} / {totalPages} 頁</div>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                下一頁
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
